import React, { useCallback, useMemo, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import { UserAvatar } from '@/components/common/Avatar'
import Button from '@/components/common/Button'
import {
  SelectionIndicator,
  selectableRowClass,
  selectableRowTitleClass,
} from '@/components/common/SelectableRow'
import { SearchInput } from '@/components/common/TextInputV2'

import type { UUID } from '@/types/Id'
import type { User } from '@/types/User'

interface SubscriberPickerProps {
  users: User[]
  orgId: UUID
  isLoading: boolean
  selectedIds: UUID[]
  currentUserId: UUID | null
  onToggle: (userId: UUID) => void
  onToggleAll: (visibleIds: UUID[]) => void
  fillHeight?: boolean
  /** Autofill name for the search input; override outside the monitors context. */
  searchName?: string
  /** Copy for the no-users-at-all state. */
  emptyMessage?: string
  /** Copy for the no-search-matches state. */
  noMatchesMessage?: string
  /**
   * Users that are always included: rendered selected but greyed out and not
   * toggleable, and excluded from select-all/deselect-all.
   */
  lockedIds?: ReadonlyArray<UUID>
  /** Small hint rendered on locked rows (e.g. "Always included"). */
  lockedHint?: string
}

export const SubscriberPicker: React.FC<SubscriberPickerProps> = ({
  users,
  orgId,
  isLoading,
  selectedIds,
  currentUserId,
  onToggle,
  onToggleAll,
  fillHeight = false,
  searchName = 'subscriber-search',
  emptyMessage = 'No users available.',
  noMatchesMessage = 'No matches.',
  lockedIds,
  lockedHint,
}) => {
  const [query, setQuery] = useState('')

  const visibleUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const filteredUsers = normalizedQuery
      ? users.filter((user) => {
          const name = `${user.first_name} ${user.last_name}`.toLowerCase()
          return (
            name.includes(normalizedQuery) ||
            user.email.toLowerCase().includes(normalizedQuery)
          )
        })
      : users
    if (currentUserId === null) return filteredUsers
    return [...filteredUsers].sort((a, b) => {
      if (a.id === currentUserId) return -1
      if (b.id === currentUserId) return 1
      return 0
    })
  }, [users, query, currentUserId])

  // O(1) membership for the per-row `checked` + partition + select-all checks,
  // which otherwise re-scan the selectedIds array once per user per keystroke.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const lockedIdSet = useMemo(() => new Set(lockedIds ?? []), [lockedIds])
  // Locked users are always included, so select-all/deselect-all only
  // operates on the remaining visible users.
  const togglableIds = useMemo(
    () =>
      visibleUsers
        .map((user) => user.id as UUID)
        .filter((id) => !lockedIdSet.has(id)),
    [visibleUsers, lockedIdSet],
  )
  const allVisibleSelected =
    togglableIds.length > 0 && togglableIds.every((id) => selectedIdSet.has(id))
  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value)
    },
    [],
  )
  const handleClearQuery = useCallback(() => {
    setQuery('')
  }, [])
  const handleToggleAll = useCallback(() => {
    onToggleAll(togglableIds)
  }, [onToggleAll, togglableIds])

  // Selected subscribers float into their own section at the top (mirrors the
  // chat share dialog) so who's already getting alerted is obvious at a glance.
  const { selectedVisible, unselectedVisible } = useMemo(() => {
    const selected: User[] = []
    const unselected: User[] = []
    for (const user of visibleUsers) {
      const id = user.id as UUID
      if (selectedIdSet.has(id) || lockedIdSet.has(id)) selected.push(user)
      else unselected.push(user)
    }
    return { selectedVisible: selected, unselectedVisible: unselected }
  }, [visibleUsers, selectedIdSet, lockedIdSet])

  const renderRow = (user: User) => {
    const userId = user.id as UUID
    const locked = lockedIdSet.has(userId)
    return (
      <SubscriberRow
        key={user.id}
        user={user}
        userId={userId}
        orgId={orgId}
        checked={locked || selectedIdSet.has(userId)}
        locked={locked}
        lockedHint={lockedHint}
        onToggle={onToggle}
      />
    )
  }

  if (isLoading && users.length === 0) {
    return (
      <div className="rounded-lg border border-primary-200 bg-white px-3 py-3 text-xs text-primary-500 dark:border-primary-800 dark:bg-default-dark dark:text-primary-400">
        Loading users…
      </div>
    )
  }
  if (users.length === 0) {
    return (
      <div className="rounded-lg border border-primary-200 bg-white px-3 py-3 text-xs text-primary-500 dark:border-primary-800 dark:bg-default-dark dark:text-primary-400">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div
      className={twMerge(
        'space-y-2',
        fillHeight && 'flex min-h-0 flex-1 flex-col',
      )}
    >
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label="Search teammates"
          name={searchName}
          autoComplete="off"
          value={query}
          onChange={handleQueryChange}
          onClear={handleClearQuery}
          placeholder="Search teammates…"
          className="h-8"
          containerClassName="flex-1"
        />
        {togglableIds.length > 0 && (
          <Button
            type="button"
            onClick={handleToggleAll}
            variant="ghost-compact"
            className="h-8 shrink-0 px-2 text-[12.5px] font-medium text-accent-700 hover:opacity-70 dark:text-accent-300"
          >
            {allVisibleSelected ? 'Deselect all' : 'Select all'}
          </Button>
        )}
      </div>
      <div
        className={twMerge(
          'space-y-3 overflow-y-auto pr-0.5',
          fillHeight ? 'min-h-0 flex-1' : 'max-h-[260px]',
        )}
      >
        {selectedVisible.length > 0 ? (
          <>
            <PickerSection title="Selected" count={selectedVisible.length}>
              {selectedVisible.map(renderRow)}
            </PickerSection>
            {unselectedVisible.length > 0 && (
              <PickerSection title="Teammates" count={unselectedVisible.length}>
                {unselectedVisible.map(renderRow)}
              </PickerSection>
            )}
          </>
        ) : (
          <div className="space-y-1.5">{visibleUsers.map(renderRow)}</div>
        )}
        {visibleUsers.length === 0 && (
          <div className="rounded-md border border-dashed border-primary-200 px-3 py-4 text-center text-[12.5px] text-primary-500 dark:border-primary-700 dark:text-primary-400">
            {noMatchesMessage}
          </div>
        )}
      </div>
    </div>
  )
}

interface PickerSectionProps {
  title: string
  count: number
  children: React.ReactNode
}

// Sticky section header (label + count), matching the chat share dialog's
// grouped sections, so "Selected" stays pinned above the teammate list.
const PickerSection: React.FC<PickerSectionProps> = ({
  title,
  count,
  children,
}) => (
  <div className="space-y-1.5">
    <div className="sticky top-0 z-10 flex items-center justify-between bg-white/95 py-1 text-[10.5px] font-bold uppercase tracking-[0.06em] text-primary-500 backdrop-blur dark:bg-default-dark/95 dark:text-primary-400">
      <span>{title}</span>
      <span className="tabular-nums">{count}</span>
    </div>
    {children}
  </div>
)

interface SubscriberRowProps {
  user: User
  userId: UUID
  orgId: UUID
  checked: boolean
  locked?: boolean
  lockedHint?: string
  onToggle: (userId: UUID) => void
}

const SubscriberRow: React.FC<SubscriberRowProps> = ({
  user,
  userId,
  orgId,
  checked,
  locked = false,
  lockedHint,
  onToggle,
}) => {
  const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email
  const handleToggle = useCallback(() => {
    onToggle(userId)
  }, [onToggle, userId])

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={locked}
      aria-pressed={checked}
      className={selectableRowClass(
        checked,
        twMerge(
          'group justify-between text-left',
          locked && 'cursor-default opacity-60',
        ),
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <UserAvatar
          userId={userId}
          orgId={orgId}
          user={user}
          width={28}
          height={28}
        />
        <div className="min-w-0">
          <div className={selectableRowTitleClass(checked)}>{fullName}</div>
          <div className="truncate text-[11.5px] text-primary-500 dark:text-primary-400">
            {user.email}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {locked && lockedHint ? (
          <span className="whitespace-nowrap text-[11px] font-medium text-primary-500 dark:text-primary-400">
            {lockedHint}
          </span>
        ) : null}
        <SelectionIndicator selected={checked} />
      </div>
    </button>
  )
}
